import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import api from '@/lib/api'
import { useTenant } from '@/contexts/AuthContext'

interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  role: string
  is_verified: boolean
}

interface Tenant {
  id: number
  name: string
  subdomain: string
}

interface Session {
  authenticated: boolean
  user?: User
  tenant?: Tenant
}

/**
 * Hook to check and manage authentication status
 */
export function useAuth() {
  const queryClient = useQueryClient()
  const { setTenant } = useTenant()

  // Query to check session status
  const {
    data: session,
    isLoading,
    error,
    refetch
  } = useQuery<Session>({
    queryKey: ['session'],
    queryFn: async () => {
      const response = await api.get('/auth/session/')
      return response.data
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Store tenant info in context whenever session data changes
  useEffect(() => {
    if (session?.authenticated && session.tenant) {
      setTenant(session.tenant)
    } else if (session?.authenticated === false) {
      setTenant(null)
    }
  }, [session, setTenant])

  // Mutation to logout
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout/')
    },
    onSuccess: () => {
      // Clear tenant info
      setTenant(null)
      // Invalidate session query
      queryClient.setQueryData(['session'], { authenticated: false })
      // Redirect to login
      window.location.href = '/login'
    },
  })

  return {
    // Session data
    isAuthenticated: session?.authenticated ?? false,
    user: session?.user,
    tenant: session?.tenant,
    
    // Loading states
    isLoading,
    error,
    
    // Actions
    refetch,
    logout: () => logoutMutation.mutate(),
    isLoggingOut: logoutMutation.isPending,
  }
}

/**
 * Hook to require authentication
 * Redirects to login if not authenticated
 */
export function useRequireAuth() {
  const auth = useAuth()
  
  // If not loading and not authenticated, redirect to login
  if (!auth.isLoading && !auth.isAuthenticated) {
    window.location.href = '/login'
  }
  
  return auth
}
